import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';

import api from '../api';
import { Provider as name } from '../config';
import { formatData } from '../../providersHelpers';
import { useGet, useGetById, usePost, usePut, useRemove, useSoftDelete } from '../../apiHooks';
import { Context as AuthContext } from '../../auth/context';

import Context from './context';

interface Props {
    children: React.ReactNode;
}

const AirportsProvider: React.FC<Props> = ({ children }: Props) => {
    const [data, setData] = useState({ ids: [], byId: {} });
    const [selected, setSelected] = useState({});
    const [error, setError] = useState({});
    const authContext = useContext(AuthContext);
    const fetched = useRef(false);

    const getById = useGetById(api, selected, setSelected, setError);

    const handleGetData = useCallback((res) => {
        setData(formatData(res));
        return res;
    }, []);
    const get = useGet(api, data, handleGetData, setError);

    // Load once on auth — airports are static lookup data
    useEffect(() => {
        if (authContext.currUser && !fetched.current) {
            fetched.current = true;
            get({});
        }
    }, [authContext.currUser]); // eslint-disable-line

    const handleSetData = useCallback((item, newData) => {
        setData(newData);
    }, []);
    const post = usePost(api, data, handleSetData, setError);

    const put = usePut(api, data, handleSetData, setError);

    const handleDeleteCallback = useCallback((id, newData) => {
        setData(newData);
        setSelected({});
        return id;
    }, []);
    const remove = useRemove(api, data, handleDeleteCallback, setError);

    const softDelete = useSoftDelete(api, data, handleDeleteCallback, setError);

    return (
        <Context.Provider
            value={{
                name,
                get,
                getById,
                post,
                put,
                data,
                setData,
                selected,
                setSelected,
                remove,
                error,
                softDelete
            }}
        >
            {children}
        </Context.Provider>
    );
};

export default AirportsProvider;
